import { useState, useEffect } from 'react';
import { Check, MoreHorizontal, Edit2, Trash2, Clock, Zap, Target } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Todo, Mission } from '@/types/todo';
import { getTimeRemaining, calculateCompletion } from '@/utils/todoHelpers';
import { formatTime } from '@/utils/timeSettings';

import ProgressBar from './ProgressBar';
import BoostModal from './BoostModal';
import MissionModal from './MissionModal';
import MissionSelectionModal from './MissionSelectionModal';
import MissionCreationModal from './MissionCreationModal';

interface TodoItemProps {
  todo: Todo;
  onComplete: (id: string) => void;
  onEdit: (id: string, newTitle: string) => void;
  onDelete: (id: string) => void;
  onBoost: (id: string, percentage: number) => void;
  missions: Mission[];
  showMissions?: boolean;
  onCreateMission: (name: string, size: any) => { id: string; mission: Mission };
  onAddToMission: (todoId: string, missionId: string, mission?: Mission) => void;
  onRemoveFromMission: (todoId: string) => void;
}

export default function TodoItem({
  todo,
  onComplete,
  onEdit,
  onDelete,
  onBoost,
  missions,
  showMissions = false,
  onAddToMission,
  onCreateMission,
  onRemoveFromMission
}: TodoItemProps) {
  const isMobile = useIsMobile();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(todo.title);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [isCompleting, setIsCompleting] = useState(false);
  const [showCompletionCard, setShowCompletionCard] = useState(false);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const [showMissionModal, setShowMissionModal] = useState(false);
  const [showMissionSelectionModal, setShowMissionSelectionModal] = useState(false);
  const [showMissionCreationModal, setShowMissionCreationModal] = useState(false);

  // Update time remaining every second for accurate display
  useEffect(() => {
    const updateTime = () => {
      setTimeRemaining(getTimeRemaining(todo.deadline));
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [todo.deadline]);

  const handleComplete = () => {
    setIsCompleting(true);
    setShowCompletionCard(true);

    // Show completion card for 0.4 seconds, then remove todo
    setTimeout(() => {
      onComplete(todo.id);
    }, 400);
  };

  const handleEdit = () => {
    if (editTitle.trim() && editTitle !== todo.title) {
      onEdit(todo.id, editTitle.trim());
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setEditTitle(todo.title);
    setIsEditing(false);
  };

  const handleBoost = (percentage: number) => {
    onBoost(todo.id, percentage);
  };

  const {
    points
  } = calculateCompletion(todo.createdAt, todo.deadline, todo.isOverdrive || false);
  const isOverdue = new Date() > todo.deadline;

  // Completion card overlay
  if (showCompletionCard) {
    return <Card className={`pt-4 pb-4 pl-6 pr-6 bg-success/10 border-success transition-all duration-300 ${!showCompletionCard ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'}`}>
        <div className="space-y-3">
          <div className="flex items-center justify-center space-x-3 py-4">
            <Check size={24} className="text-success flex-shrink-0" strokeWidth={2} />
            <div className="text-left">
              <h3 className="text-base font-semibold text-foreground">Task Completed!</h3>
              <p className="text-success text-base font-medium">+{points}pts earned</p>
            </div>
          </div>
        </div>
      </Card>;
  }

  return (
    <>
      <Card className={`pt-4 pb-4 pl-6 pr-6 transition-all duration-300 ${!showCompletionCard && isCompleting ? 'opacity-0 transform scale-95' : 'opacity-100 transform scale-100'}`}>
        <div className="space-y-3">
          {/* Title and Actions */}
          <div className="flex items-start justify-between">
            {isEditing ? <div className="flex items-center gap-2 flex-1">
                <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleEdit()} onBlur={handleEdit} className="flex-1 text-base" style={{
              fontSize: '16px'
            }} autoFocus />
                <Button variant="ghost" size="xs" onClick={handleEditCancel} className="text-muted-foreground">
                  Cancel
                </Button>
              </div> : <>
                <div className="flex-1 mr-3">
                  <h3 className="font-medium text-foreground">
                    {todo.title}
                  </h3>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Created at {formatTime(todo.createdAt)}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Button onClick={() => setShowBoostModal(true)} variant="outline" size="xs" aria-label="Boost task">
                    <Zap size={16} />
                  </Button>
                  <Button 
                    onClick={handleComplete} 
                    variant="outline" 
                    size="xs" 
                    className={isMobile ? "px-2" : "px-3"}
                    aria-label={isMobile ? "Complete" : undefined}
                  >
                    <Check size={16} />
                    {!isMobile && "Complete"}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="xs" className="text-muted-foreground" aria-label="More options">
                        <MoreHorizontal size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setIsEditing(true)}>
                        <Edit2 className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onDelete(todo.id)} className="text-destructive">
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </>}
          </div>
          
          {/* Time left display */}
          {!isEditing && <div className="flex items-center justify-between text-sm text-muted-foreground">
              <div className="flex items-center">
                <Clock size={16} className="mr-1" />
                <span className="text-xs text-muted-foreground">
                  {timeRemaining === 'Overdue' ? timeRemaining : `${timeRemaining} left`}
                </span>
              </div>
              
              {/* Mission Icon or Badge */}
              {todo.missionId && showMissions ? (
                <Button 
                  variant="link" 
                  size="xs"
                  className="text-xs h-auto p-0 text-muted-foreground"
                  onClick={() => setShowMissionModal(true)}
                >
                  {(() => {
                    const mission = missions.find(m => m.id === todo.missionId);
                    return mission ? mission.name : '';
                  })()}
                </Button>
              ) : showMissions ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    if (missions.length === 0) {
                      setShowMissionCreationModal(true);
                    } else {
                      setShowMissionSelectionModal(true);
                    }
                  }}
                >
                  <Target className="w-3 h-3" />
                </Button>
              ) : null}
            </div>}
          
          {/* Progress Bar */}
          {!isEditing && <ProgressBar createdAt={todo.createdAt} deadline={todo.deadline} isOverdrive={todo.isOverdrive} />}
          
        </div>
      </Card>

      {/* Sibling Modals - Outside of Card component */}
      <BoostModal 
        isOpen={showBoostModal}
        onClose={() => setShowBoostModal(false)}
        onBoost={handleBoost}
        currentDeadline={todo.deadline}
      />
      
      <MissionModal 
        isOpen={showMissionModal}
        onClose={() => setShowMissionModal(false)}
        todo={todo}
        missions={missions}
        onChangeMission={() => setShowMissionSelectionModal(true)}
        onRemoveMission={() => onRemoveFromMission(todo.id)}
      />
      
      <MissionSelectionModal
        isOpen={showMissionSelectionModal}
        onClose={() => setShowMissionSelectionModal(false)}
        missions={missions}
        onSelectMission={(missionId) => onAddToMission(todo.id, missionId)}
        onCreateNew={() => setShowMissionCreationModal(true)}
      />
      
      <MissionCreationModal
        isOpen={showMissionCreationModal}
        onClose={() => setShowMissionCreationModal(false)}
        onCreateMission={(name, size) => {
          const { id: missionId, mission } = onCreateMission(name, size);
          onAddToMission(todo.id, missionId, mission);
          setShowMissionCreationModal(false);
          setShowMissionSelectionModal(false);
        }}
      />
    </>
  );
}
